import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Title,
  Text,
  Card,
  Badge,
  Button,
} from '@tremor/react';
import { FlaskConical, FileCheck, AlertTriangle } from 'lucide-react';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { api } from '@/lib/api';

interface TestRun {
  run_id: string;
  started_at: string | null;
  case_count: number;
  has_report: boolean;
  has_summary: boolean;
  size_bytes: number;
}

interface TestRunDetail extends TestRun {
  report_md: string | null;
  summary: Record<string, unknown> | null;
  cases: string[];
}

interface TestCase {
  name: string;
  has_result: boolean;
  has_pdf: boolean;
  metrics: Record<string, unknown> | null;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function TestingPage() {
  const [selectedRun, setSelectedRun] = useState<string | null>(null);

  const runs = useQuery<TestRun[]>({
    queryKey: ['testing.runs'],
    queryFn: async () => (await api().get('/testing/runs')).data,
  });

  const detail = useQuery<TestRunDetail>({
    queryKey: ['testing.run', selectedRun],
    queryFn: async () =>
      (await api().get(`/testing/runs/${selectedRun}`)).data,
    enabled: !!selectedRun,
  });

  const cases = useQuery<TestCase[]>({
    queryKey: ['testing.cases', selectedRun],
    queryFn: async () =>
      (await api().get(`/testing/runs/${selectedRun}/cases`)).data,
    enabled: !!selectedRun,
  });

  if (runs.isLoading) return <LoadingSkeleton />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Title>Testing</Title>
          <Text>
            Eval runs do JusQuant (acurácia + eficiência). Roda{' '}
            <code>eval/results/</code> no servidor.
          </Text>
        </div>
        <Badge color="slate" icon={FlaskConical}>
          {(runs.data ?? []).length} runs
        </Badge>
      </div>

      {(runs.data ?? []).length === 0 ? (
        <Card className="border border-dashed border-slate-700">
          <div className="flex items-center gap-2 text-slate-400">
            <AlertTriangle className="h-4 w-4" /> Nenhum eval run encontrado em{' '}
            <code>eval/results/</code>. Crie um com{' '}
            <code>tools/run_juand_eval.sh</code>.
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Run list */}
          <Card className="lg:col-span-1">
            <Title>Runs</Title>
            <div className="mt-3 space-y-1">
              {(runs.data ?? []).map((r) => {
                const active = r.run_id === selectedRun;
                return (
                  <button
                    key={r.run_id}
                    onClick={() => setSelectedRun(r.run_id)}
                    className={`w-full rounded-md p-2 text-left text-sm transition ${
                      active
                        ? 'bg-blue-600/20 text-blue-300'
                        : 'text-slate-300 hover:bg-slate-800/60'
                    }`}
                  >
                    <div className="font-mono text-xs">{r.run_id}</div>
                    <div className="text-[11px] text-slate-500">
                      {r.case_count} casos · {fmtBytes(r.size_bytes)}
                      {r.has_report && (
                        <Badge color="green" size="xs" className="ml-2">
                          report
                        </Badge>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Run detail */}
          <div className="space-y-6 lg:col-span-2">
            {!selectedRun && (
              <Card className="border border-dashed border-slate-700">
                <Text className="text-slate-400">
                  Selecione um run à esquerda.
                </Text>
              </Card>
            )}
            {selectedRun && detail.isLoading && <LoadingSkeleton />}
            {selectedRun && detail.data && (
              <>
                <Card>
                  <div className="flex items-center justify-between">
                    <Title>{detail.data.run_id}</Title>
                    <Button
                      size="xs"
                      variant="secondary"
                      onClick={() => {
                        navigator.clipboard
                          ?.writeText(detail.data?.run_id ?? '')
                          .catch(() => undefined);
                      }}
                    >
                      copy id
                    </Button>
                  </div>
                  <Text>
                    {detail.data.case_count} casos ·{' '}
                    {detail.data.has_summary && (
                      <Badge color="blue" size="xs">
                        summary
                      </Badge>
                    )}{' '}
                    {detail.data.has_report && (
                      <Badge color="green" size="xs">
                        report.md
                      </Badge>
                    )}
                  </Text>
                </Card>

                {detail.data.cases.length > 0 && (
                  <Card>
                    <Title>Casos</Title>
                    <div className="mt-3 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="text-left text-slate-400">
                          <tr>
                            <th className="pb-2">Nome</th>
                            <th className="pb-2">Result</th>
                            <th className="pb-2">PDF</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(cases.data ?? []).map((c) => (
                            <tr
                              key={c.name}
                              className="border-t border-slate-800/60"
                            >
                              <td className="py-2 font-mono text-xs">
                                {c.name}
                              </td>
                              <td className="py-2">
                                {c.has_result ? (
                                  <FileCheck className="h-4 w-4 text-emerald-400" />
                                ) : (
                                  <span className="text-slate-500">—</span>
                                )}
                              </td>
                              <td className="py-2">
                                {c.has_pdf ? (
                                  <Badge color="green" size="xs">
                                    pdf
                                  </Badge>
                                ) : (
                                  <span className="text-slate-500">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}

                {detail.data.report_md && (
                  <Card>
                    <Title>report.md</Title>
                    <pre className="mt-3 max-h-[600px] overflow-auto whitespace-pre-wrap rounded-md bg-slate-950 p-3 font-mono text-xs text-slate-200">
                      {detail.data.report_md}
                    </pre>
                  </Card>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
